/**
 * "우리 지역 장례 비용" 카드(LocalCostCard) A/B 3개 (2026-10-16 영민). 카드 → www 장례 비용 계산기 /match/cost-calculator/.
 * - #361 가이드북 + 후기(목록, 상세). A = 기존 패키지 배너, B = 카드로 교체
 * - #362 호스피스 상세 + 요양 상세. A = 카드 없음, B = 상단 배너 아래에 카드 추가
 * - #363 장지 상세. A = 장례 준비 체크리스트(FacilityPrepChecklistSection), B = 그 자리에 카드 (#350 A 채택 후 결정, 2026-10-16 영민)
 * 같은 실험 키를 쓰는 지면끼리는 한 사람이 같은 안을 본다. 키가 0 이면 훅을 부르지 않고 A 만 그린다.
 */
import { customEvent } from "../utils/customEvent";

export type CostCardSurface =
  | "guidebook"
  | "review_list"
  | "review_detail"
  | "hospice"
  | "care"
  | "facility";

export const COST_CARD_KEYS: Record<CostCardSurface, number> = {
  guidebook: 361,
  review_list: 361,
  review_detail: 361,
  hospice: 362,
  care: 362,
  facility: 363,
};

/** 지면별 개별 이벤트 path. click-{path}-costcard-calc 로 발화한다. 공통 이벤트는 click-cost_calc-entry_card (text = 지면) */
export const COST_CARD_EVENT_PATH = {
  guidebook: "guidebook",
  review_list: "review",
  review_detail: "review_detail",
  hospice: "hospice_detail",
  care: "care_detail",
  facility: "facility_detail",
} as const;

/** 지면별 utm_campaign. 계산기는 from 과 함께 리드 메모에 남긴다 */
export const COST_CARD_UTM: Record<CostCardSurface, string> = {
  guidebook: "goi_cc_gdb",
  review_list: "goi_cc_rvl",
  review_detail: "goi_cc_rvd",
  hospice: "goi_cc_hsp",
  care: "goi_cc_care",
  facility: "goi_cc_fac",
};

/** 계산기 진입 주소. from 은 계산기 안 이벤트와 리드 메모용, utm 은 지면별 유입 구분용 */
export const getCostCalculatorUrl = (surface: CostCardSurface) =>
  `/match/cost-calculator/?from=${surface}&utm_campaign=${COST_CARD_UTM[surface]}`;

/**
 * @param from - router.query.from (string | string[] | undefined)
 * @returns 카드가 붙은 지면, 헤더 "장례견적"이면 "header". 모르면 "direct"
 */
export const parseCostCardSurface = (
  from: string | string[] | undefined
): CostCardSurface | "header" | "direct" => {
  if (from === "header") return "header";
  return typeof from === "string" &&
    Object.prototype.hasOwnProperty.call(COST_CARD_KEYS, from)
    ? (from as CostCardSurface)
    : "direct";
};

/**
 * 헤더 "장례견적"을 카드 실험 B 처치에 포함한다 (2026-10-17 영민).
 * 헤더는 모든 페이지에 있어 여기서 배정 훅을 부르면 카드를 못 본 사람까지 노출로 잡힌다.
 * 그래서 카드 게이트가 정해진 안을 기록해 두고, 헤더는 그 기록을 읽기만 한다.
 * 실험을 끝낼 때 COST_CARD_KEYS 를 0 으로 바꾸면 그 키의 기록은 읽지 않아 헤더도 같이 원래대로 돌아간다.
 */
const COST_CARD_ARM_STORAGE_KEY = "cost_card_arm";
/** 같은 탭에서 기록이 바뀐 걸 헤더에 알린다. storage 이벤트는 다른 탭에만 오기 때문 */
export const COST_CARD_ARM_EVENT = "cost_card_arm_change";
export const COST_CALC_HEADER_URL =
  "/match/cost-calculator/?from=header&utm_campaign=goi_cc_hdr";

const readCostCardArms = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(COST_CARD_ARM_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

/** 카드 게이트에서 A/B 가 정해졌을 때 부른다. Z(미할당)는 기록하지 않는다 */
export const rememberCostCardArm = (experimentKey: number, arm: string) => {
  if (arm !== "A" && arm !== "B") return;
  try {
    const arms = readCostCardArms();
    if (arms[experimentKey] === arm) return;
    arms[experimentKey] = arm;
    localStorage.setItem(COST_CARD_ARM_STORAGE_KEY, JSON.stringify(arms));
    window.dispatchEvent(new Event(COST_CARD_ARM_EVENT));
  } catch {
    // 저장이 막힌 브라우저는 헤더가 기존대로 견적으로 간다
  }
};

/**
 * 살아 있는 실험(키 > 0) 중 하나라도 B 면 true. 이미 /match 안에 있는 사람은 견적 흐름을 끊지 않도록 false.
 * localStorage 를 읽으므로 mount 이후(useEffect)에서 부른다.
 */
export const shouldHeaderGoToCostCalc = (): boolean => {
  if (typeof window === "undefined") return false;
  if (window.location.pathname.startsWith("/match")) return false;
  const arms = readCostCardArms();
  return Object.values(COST_CARD_KEYS).some(
    (key) => key > 0 && arms[key] === "B"
  );
};

export const COST_CARD_DEFAULT_TITLE = "우리 지역 장례 비용,\n얼마일까요?";
const MUBINSO_TITLE = "무빈소 장례 비용,\n우리 지역은 얼마일까요?";
const FAMILY_TITLE = "가족장 비용,\n우리 지역은 얼마일까요?";
const JANGJI_TITLE = "장지까지 더하면\n우리 지역은 얼마일까요?";
const SANGJO_TITLE = "상조까지 더하면\n우리 지역은 얼마일까요?";

/** `${category}/${index}` → 글 주제에 맞춘 제목. 없는 글은 기본 제목 */
const GUIDEBOOK_TITLES: Record<string, string> = {
  "before/mubinso": MUBINSO_TITLE,
  "ongoing/3": MUBINSO_TITLE, // 무빈소 장례의 모든 것
  "before/family-funeral": FAMILY_TITLE,
  "ongoing3/0": JANGJI_TITLE, // 장지의 종류와 비용
  "ongoing3/1": JANGJI_TITLE, // 봉안 시설(납골당) 고르기
  "ongoing/12": JANGJI_TITLE, // 봉안시설의 종류와 장단점
  "after/0": JANGJI_TITLE, // 봉안함(유골함) 집 보관
  "before/18": "수급자 장례,\n우리 지역은 얼마일까요?",
  "before/4": SANGJO_TITLE, // 상조회사 vs 장례식장
  "before/15": SANGJO_TITLE, // 상조회사 순위
};

/**
 * 지금 장례를 치르는 상주가 읽는 글. 미리 알아보는 사람용 카드라 실험에서 빼고 A 를 유지한다.
 * ongoing1 은 1일차(사망 서류, 1일차, 식장 계약, 부고 양식) 묶음이라 통째로 뺀다.
 */
const GUIDEBOOK_EXCLUDED = new Set(["ongoing/11", "ongoing/14"]); // 운구, 입관식
const GUIDEBOOK_EXCLUDED_CATEGORIES = new Set(["ongoing1"]);

/** null 이면 실험 대상이 아닌 글이다 (배정도 하지 않는다) */
export const getGuidebookCostCardTitle = (
  category: string,
  index: string
): string | null => {
  if (GUIDEBOOK_EXCLUDED_CATEGORIES.has(category)) return null;
  const key = `${category}/${index}`;
  if (GUIDEBOOK_EXCLUDED.has(key)) return null;
  return GUIDEBOOK_TITLES[key] ?? COST_CARD_DEFAULT_TITLE;
};

/**
 * 후기 본문 키워드로 제목을 고른다. 후기의 상품 정보(sangjo_type)는 2,745건 중 60건만 채워져 있어 쓰지 않는다.
 * 목록 화면은 본문이 없어 기본 제목.
 */
export const getReviewCostCardTitle = (text?: string): string => {
  if (text?.includes("무빈소")) return MUBINSO_TITLE;
  if (text?.includes("가족장")) return FAMILY_TITLE;
  return COST_CARD_DEFAULT_TITLE;
};

/**
 * 카드 실험 공통 슬롯 클릭 (click-cost_card_ab-slot_cta). A·B 가 같은 키를 쏴서 Hackle 지표 하나로 클릭률을 비교한다.
 * A 는 그 자리 기존 CTA(가이드북·후기 "패키지 추천받기", 장지 상세 체크리스트 CTA), B 는 카드 버튼.
 * #362(호스피스·요양) A 는 그 자리에 아무것도 없어 B 만 쏜다. 기존 지면별 이벤트는 그대로 둔다.
 */
export const trackCostCardSlotCta = (
  surface: CostCardSurface,
  arm: "A" | "B"
) =>
  customEvent({
    newGtm: {
      path: "cost_card_ab",
      trigger_and_target: "slot_cta",
      text: surface,
      properties: { arm },
    },
  });
